import { useState } from 'react'
import axios from 'axios'

interface SearchTabProps {
    session: any
}

interface SearchResult {
    chunk_id: string
    content: string
    score: number
    metadata: {
        document_id: string
        filename: string
        page?: number
    }
}

const API_BASE = import.meta.env.VITE_RAG_API_URL || 'http://localhost:8000'

export function SearchTab({ session }: SearchTabProps) {
    const [query, setQuery] = useState('')
    const [topK, setTopK] = useState(10)
    const [minScore, setMinScore] = useState(0.5)
    const [results, setResults] = useState<SearchResult[]>([])
    const [isSearching, setIsSearching] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const handleSearch = async () => {
        if (!session?.access_token || !query.trim()) return

        setIsSearching(true)
        setError(null)

        try {
            const config = {
                headers: { Authorization: `Bearer ${session.access_token}` }
            }

            const payload = {
                query: query.trim(),
                top_k: topK,
                min_score: minScore,
                scope: 'user'
            }

            const response = await axios.post(`${API_BASE}/retrieval/search`, payload, config)
            setResults(response.data.chunks || [])
        } catch (err: any) {
            console.error('Search failed:', err)
            setError(err.response?.data?.detail || 'Search failed')
            setResults([])
        } finally {
            setIsSearching(false)
        }
    }

    const highlightText = (text: string, query: string) => {
        if (!query.trim()) return text

        const parts = text.split(new RegExp(`(${query})`, 'gi'))
        return parts.map((part, i) =>
            part.toLowerCase() === query.toLowerCase()
                ? <mark key={i} className="bg-yellow-500/30 text-yellow-200">{part}</mark>
                : part
        )
    }

    const getScoreColor = (score: number) => {
        if (score >= 0.8) return 'text-green-400'
        if (score >= 0.6) return 'text-yellow-400'
        return 'text-orange-400'
    }

    return (
        <div className="flex-1 flex flex-col h-full">
            {/* Search Controls */}
            <div className="p-6 border-b border-white/10 bg-[#0A0A0A]">
                <h2 className="text-xl font-bold text-white mb-4">Retrieval Search</h2>
                <p className="text-gray-400 text-sm mb-6">
                    Search for relevant chunks without LLM generation
                </p>

                {/* Query Input */}
                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                        Search Query
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                            placeholder="Enter your search query..."
                            className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                        />
                        <button
                            onClick={handleSearch}
                            disabled={isSearching || !query.trim()}
                            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
                        >
                            {isSearching ? 'Searching...' : 'Search'}
                        </button>
                    </div>
                </div>

                {/* Filters */}
                <div className="grid grid-cols-2 gap-4">
                    {/* Top K Slider */}
                    <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">
                            Top K Results: {topK}
                        </label>
                        <input
                            type="range"
                            min="1"
                            max="100"
                            value={topK}
                            onChange={(e) => setTopK(parseInt(e.target.value))}
                            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                            <span>1</span>
                            <span>100</span>
                        </div>
                    </div>

                    {/* Min Score Slider */}
                    <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">
                            Min Score: {minScore.toFixed(2)}
                        </label>
                        <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.05"
                            value={minScore}
                            onChange={(e) => setMinScore(parseFloat(e.target.value))}
                            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                            <span>0.0</span>
                            <span>1.0</span>
                        </div>
                    </div>
                </div>

                {/* Error Display */}
                {error && (
                    <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-300 text-sm">
                        {error}
                    </div>
                )}
            </div>

            {/* Results */}
            <div className="flex-1 overflow-y-auto p-6 bg-[#0C0C0C]">
                {results.length === 0 && !isSearching && (
                    <div className="text-center text-gray-400 py-12">
                        {query ? 'No results found' : 'Enter a query to search'}
                    </div>
                )}

                {isSearching && (
                    <div className="text-center text-gray-400 py-12">
                        Searching...
                    </div>
                )}

                <div className="space-y-4">
                    {results.map((result, idx) => (
                        <div
                            key={result.chunk_id || idx}
                            className="bg-white/5 border border-white/10 rounded-lg p-4 hover:bg-white/10 transition-all"
                        >
                            {/* Header */}
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-3">
                                    <span className="text-xs font-mono text-gray-500">
                                        #{idx + 1}
                                    </span>
                                    <span className="text-sm text-gray-400">
                                        {result.metadata?.filename || 'Unknown'}
                                        {result.metadata?.page && ` (p.${result.metadata.page})`}
                                    </span>
                                </div>
                                <div className={`text-sm font-semibold ${getScoreColor(result.score)}`}>
                                    Score: {result.score.toFixed(3)}
                                </div>
                            </div>

                            {/* Content */}
                            <div className="text-sm text-gray-300 leading-relaxed">
                                {highlightText(result.content, query)}
                            </div>

                            {/* Footer */}
                            <div className="mt-3 pt-3 border-t border-white/10 flex items-center gap-4 text-xs text-gray-500">
                                <span>Chunk ID: {result.chunk_id?.substring(0, 8)}...</span>
                                <span>Doc ID: {result.metadata?.document_id?.substring(0, 8)}...</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}
